import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Database } from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Data Archive card — downloads a ZIP containing every collection as JSON
// for offsite backup, accountant handoff, or migration.
const DataArchiveCard = () => {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importMode, setImportMode] = useState("merge");
  const [includeUsers, setIncludeUsers] = useState(true);
  const [importPreview, setImportPreview] = useState(null);
  const [importing, setImporting] = useState(false);
  const [replaceAck, setReplaceAck] = useState(false);
  const importInputRef = useRef(null);

  const loadSummary = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/export-archive/summary`);
      setSummary(r.data);
    } catch (e) {
      toast.error("Couldn't load archive summary: " + (e.response?.data?.detail || e.message));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSummary();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const download = async () => {
    setDownloading(true);
    try {
      const res = await axios.get(`${API}/admin/export-archive`, { responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/zip" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const stamp = new Date().toISOString().replace(/[-:]/g, "").replace(/\..+/, "Z");
      a.download = `crest-pos-archive-${stamp}.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Archive downloaded — store it safely (offsite drive, cloud, etc.)");
    } catch (e) {
      toast.error("Archive download failed: " + (e.response?.data?.detail || e.message));
    } finally {
      setDownloading(false);
    }
  };

  // Pick a ZIP → immediately dry-run against the API so the confirm dialog
  // shows real per-collection counts instead of just filenames.
  const chooseFile = async (f) => {
    if (!f) return;
    if (!/\.zip$/i.test(f.name)) {
      toast.error("Please pick a .zip file produced by Download Complete Archive.");
      return;
    }
    setImportFile(f);
    setImportPreview(null);
    setReplaceAck(false);
    const fd = new FormData();
    fd.append("file", f);
    try {
      const r = await axios.post(
        `${API}/admin/import-archive?mode=merge&dry_run=true&include_users=true`,
        fd,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      setImportPreview(r.data);
    } catch (e) {
      toast.error("Couldn't read that ZIP: " + (e.response?.data?.detail || e.message));
      setImportFile(null);
    }
  };

  const runImport = async () => {
    if (!importFile) return;
    if (importMode === "replace" && !replaceAck) {
      toast.error("Confirm the backup checkbox before running a replace import.");
      return;
    }
    setImporting(true);
    const fd = new FormData();
    fd.append("file", importFile);
    try {
      const r = await axios.post(
        `${API}/admin/import-archive?mode=${importMode}&dry_run=false&include_users=${includeUsers}`,
        fd,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      const t = r.data.totals || {};
      toast.success(
        `Import complete — ${t.inserted || 0} inserted, ${t.updated || 0} updated, ${t.deleted || 0} deleted.`,
        { duration: 6000 }
      );
      setImportFile(null);
      setImportPreview(null);
      setReplaceAck(false);
      if (importInputRef.current) importInputRef.current.value = "";
      loadSummary();
    } catch (e) {
      toast.error("Import failed: " + (e.response?.data?.detail || e.message));
    } finally {
      setImporting(false);
    }
  };

  // Order matters — group config first, ops last, so the table reads like a
  // mini ER diagram from least-volatile to most-volatile.
  const ORDER = [
    "locations", "rooms", "tables", "store_settings", "equipment", "networks",
    "users", "customers",
    "menu_items", "modifier_templates",
    "loyalty_customers", "loyalty_tier_config", "loyalty_perk_redemptions",
    "orders", "transactions", "refunds", "reservations", "feedback",
    "shifts", "timeclock", "cash_drawer_events",
  ];

  return (
    <Card data-testid="data-archive-card">
      <CardHeader className="flex flex-row items-start justify-between gap-4">
        <div>
          <CardTitle>Complete Data Archive</CardTitle>
          <CardDescription>
            Downloads every operational collection (menus, orders, customers, loyalty, shifts,
            transactions, equipment, settings) as a single ZIP of JSON files. Use this for
            offsite backups, accountant handoffs, or migrating to a new server. Generated
            on-demand — does not affect live operations.
          </CardDescription>
        </div>
        <Button
          variant="outline"
          onClick={loadSummary}
          disabled={loading || downloading}
          data-testid="archive-rescan-btn"
        >
          {loading ? "Counting…" : "Refresh"}
        </Button>
      </CardHeader>
      <CardContent className="space-y-4">
        {!summary ? (
          <div className="text-sm text-muted-foreground">Counting records…</div>
        ) : (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-2">
              {ORDER.map((key) => (
                <div
                  key={key}
                  className="rounded-md border border-border bg-secondary/20 px-3 py-2"
                  data-testid={`archive-count-${key}`}
                >
                  <div className="text-[10px] uppercase tracking-wide text-muted-foreground truncate">
                    {key.replace(/_/g, " ")}
                  </div>
                  <div className="font-mono text-base font-semibold">
                    {(summary.counts[key] ?? 0).toLocaleString()}
                  </div>
                </div>
              ))}
            </div>
            <div className="rounded-lg border border-border bg-secondary/30 px-4 py-3 text-sm flex flex-wrap items-baseline gap-x-4 gap-y-1">
              <span className="font-mono text-lg font-semibold" data-testid="archive-total-records">
                {summary.total_records.toLocaleString()}
              </span>
              <span className="text-muted-foreground">records across</span>
              <span className="font-mono">{summary.collections}</span>
              <span className="text-muted-foreground">collections</span>
            </div>
            <div className="flex flex-wrap justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => importInputRef.current?.click()}
                disabled={downloading || importing}
                className="gap-2"
                data-testid="archive-import-pick-btn"
              >
                <Database size={18} />
                Import from ZIP…
              </Button>
              <input
                ref={importInputRef}
                type="file"
                accept=".zip,application/zip"
                className="hidden"
                onChange={(e) => chooseFile(e.target.files?.[0])}
                data-testid="archive-import-file-input"
              />
              <Button
                onClick={download}
                disabled={downloading || loading}
                className="gap-2"
                data-testid="archive-download-btn"
              >
                <Database size={18} />
                {downloading ? "Building archive…" : "Download Complete Archive (ZIP)"}
              </Button>
            </div>
          </>
        )}
      </CardContent>

      <Dialog open={!!importFile} onOpenChange={(o) => { if (!o) { setImportFile(null); setImportPreview(null); setReplaceAck(false); if (importInputRef.current) importInputRef.current.value = ""; } }}>
        <DialogContent className="max-w-2xl" data-testid="archive-import-dialog">
          <DialogHeader>
            <DialogTitle>Import Archive — {importFile?.name}</DialogTitle>
          </DialogHeader>
          {!importPreview ? (
            <div className="py-6 text-sm text-muted-foreground">Reading archive…</div>
          ) : (
            <div className="space-y-4 py-2">
              {importPreview.manifest?.generated_at && (
                <div className="text-xs text-muted-foreground">
                  Source snapshot: <span className="font-mono">{importPreview.manifest.generated_at}</span>
                  {importPreview.manifest.exported_by?.name && (
                    <> · by {importPreview.manifest.exported_by.name}</>
                  )}
                </div>
              )}
              <div className="rounded-md border border-border overflow-hidden text-sm">
                <div className="grid grid-cols-2 bg-secondary/40 px-3 py-1.5 text-[10px] uppercase tracking-wide text-muted-foreground">
                  <div>Collection</div>
                  <div className="text-right">Records to import</div>
                </div>
                <div className="max-h-64 overflow-y-auto divide-y divide-border">
                  {ORDER.map((k) => {
                    const info = importPreview.results?.[k];
                    if (!info) return null;
                    const count = info.input ?? 0;
                    const skipped = info.skipped;
                    return (
                      <div
                        key={k}
                        className={`grid grid-cols-2 px-3 py-1.5 ${skipped ? "opacity-50" : ""}`}
                        data-testid={`archive-import-preview-${k}`}
                      >
                        <div className="truncate">{k.replace(/_/g, " ")}</div>
                        <div className="text-right font-mono">
                          {skipped ? <span className="text-xs">— ({info.reason})</span> : count.toLocaleString()}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className="space-y-3">
                <div>
                  <label className="text-xs uppercase tracking-wide text-muted-foreground">Mode</label>
                  <div className="grid grid-cols-2 gap-2 mt-1">
                    <button
                      type="button"
                      onClick={() => setImportMode("merge")}
                      className={`rounded-md border px-3 py-2 text-left text-sm ${importMode === "merge" ? "border-primary bg-primary/5" : "border-border bg-card"}`}
                      data-testid="archive-import-mode-merge"
                    >
                      <div className="font-medium">Merge (safe)</div>
                      <div className="text-xs text-muted-foreground">Upsert by id. Keep any local records not in the archive.</div>
                    </button>
                    <button
                      type="button"
                      onClick={() => setImportMode("replace")}
                      className={`rounded-md border px-3 py-2 text-left text-sm ${importMode === "replace" ? "border-red-500 bg-red-50 dark:bg-red-950/30" : "border-border bg-card"}`}
                      data-testid="archive-import-mode-replace"
                    >
                      <div className="font-medium text-red-700 dark:text-red-400">Replace (destructive)</div>
                      <div className="text-xs text-muted-foreground">For each collection in the ZIP, wipe local data then insert.</div>
                    </button>
                  </div>
                </div>
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input
                    type="checkbox"
                    checked={includeUsers}
                    onChange={(e) => setIncludeUsers(e.target.checked)}
                    data-testid="archive-import-include-users"
                  />
                  <span>Include <strong>users &amp; roles</strong> (uncheck to keep the local employee roster)</span>
                </label>
                {importMode === "replace" && (
                  <label className="flex items-start gap-2 text-sm cursor-pointer rounded-md border border-red-200 bg-red-50 dark:bg-red-950/30 dark:border-red-900 px-3 py-2">
                    <input
                      type="checkbox"
                      checked={replaceAck}
                      onChange={(e) => setReplaceAck(e.target.checked)}
                      className="mt-0.5"
                      data-testid="archive-import-replace-ack"
                    />
                    <span className="text-red-900 dark:text-red-200">
                      I understand this will delete existing rows in every collection present in the ZIP.
                      I have downloaded a current archive as a rollback point (or trust my nightly backup).
                    </span>
                  </label>
                )}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => { setImportFile(null); setImportPreview(null); setReplaceAck(false); if (importInputRef.current) importInputRef.current.value = ""; }}
              disabled={importing}
              data-testid="archive-import-cancel"
            >
              Cancel
            </Button>
            <Button
              onClick={runImport}
              disabled={importing || !importPreview || (importMode === "replace" && !replaceAck)}
              data-testid="archive-import-confirm"
            >
              {importing ? "Importing…" : `Run ${importMode === "replace" ? "Replace" : "Merge"} Import`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default DataArchiveCard;

